import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Search, Mail, Phone, Tag, Trash2, X, MessageSquare,
  TrendingUp, Filter, ExternalLink, Plus, RefreshCw,
} from "lucide-react";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const SOURCE_BADGES = {
  newsletter: "bg-[#0E7C8F] text-white",
  contact_form: "bg-[#1E3557] text-white",
  stripe: "bg-[#0B7C5C] text-white",
  landing: "bg-[#E89B6C] text-[#0B192C]",
  unknown: "bg-[#E5E1DA] text-[#0B192C]",
};

export default function LeadsTab() {
  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [activeLead, setActiveLead] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (filterSource) params.set("source", filterSource);
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/leads?${params}`, { headers: authHdr() }),
        axios.get(`${API}/admin/leads/stages`, { headers: authHdr() }),
      ]);
      setData(a.data);
      setStages(b.data.stages || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, filterSource]);

  const syncExisting = async () => {
    if (!window.confirm("Importare tutti gli iscritti/contatti/transazioni esistenti come Lead?")) return;
    try {
      const { data } = await axios.post(`${API}/admin/leads/sync-existing`, {}, { headers: authHdr() });
      alert(`Importati ${data.created} nuovi lead.`);
      load();
    } catch (err) { alert(formatApiError(err)); }
  };

  const moveStage = async (leadId, stage) => {
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { stage }, { headers: authHdr() });
      load();
    } catch (err) { alert(formatApiError(err)); }
  };

  if (loading && !data) return <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={16} /> Caricamento...</div>;
  if (error) return <p className="text-sm text-red-700">{error}</p>;

  const leadsByStage = (stageId) => (data?.items || []).filter((l) => l.stage === stageId);

  return (
    <div data-testid="admin-leads-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-serif-display text-3xl text-[#0B192C]">Lead CRM</h2>
          <span className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">{data?.total ?? 0} lead</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="leads-refresh-btn" className="inline-flex items-center gap-1 px-3 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-white">
            <RefreshCw size={12} /> Aggiorna
          </button>
          <button onClick={syncExisting} data-testid="leads-sync-btn" className="inline-flex items-center gap-1 px-3 py-2 border border-[#E89B6C] text-[#E89B6C] text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C]">
            <Plus size={12} /> Importa esistenti
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A4F6B]" />
          <input type="text" placeholder="Cerca per email, nome, telefono, campagna..."
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="leads-search-input"
            className="w-full pl-9 pr-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
        </div>
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
          data-testid="leads-filter-source"
          className="px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]">
          <option value="">Tutte le fonti</option>
          {(data?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Kanban board */}
      <div className="overflow-x-auto pb-2 -mx-2">
        <div className="flex gap-3 px-2 min-w-min">
          {stages.map((s) => {
            const summary = (data?.stages_summary || []).find((x) => x.id === s.id) || { count: 0, value: 0 };
            const visibleLeads = leadsByStage(s.id);
            return (
              <div
                key={s.id}
                className="w-72 flex-shrink-0 bg-[#F4E8D6]/30 border border-[#E5E1DA]"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (draggingId) moveStage(draggingId, s.id);
                  setDraggingId(null);
                }}
                data-testid={`stage-col-${s.id}`}
              >
                <div className="p-3 border-b border-[#E5E1DA]" style={{ borderTop: `3px solid ${s.color}` }}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm" style={{ color: s.color }}>{s.label}</p>
                    <span className="px-2 py-0.5 bg-white text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
                      {summary.count}
                    </span>
                  </div>
                  {summary.value > 0 && (
                    <p className="text-[10px] text-[#3A4F6B] mt-1">€ {summary.value.toFixed(2)}</p>
                  )}
                </div>
                <div className="p-2 space-y-2 min-h-[180px] max-h-[60vh] overflow-y-auto">
                  {visibleLeads.length === 0 && (
                    <p className="text-[10px] text-[#3A4F6B]/60 uppercase tracking-[0.18em] text-center py-4">Nessun lead</p>
                  )}
                  {visibleLeads.map((l) => (
                    <LeadCard
                      key={l.id}
                      lead={l}
                      onOpen={() => setActiveLead(l)}
                      onDragStart={() => setDraggingId(l.id)}
                      onDragEnd={() => setDraggingId(null)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeLead && (
        <LeadDetailModal
          leadId={activeLead.id}
          stages={stages}
          onClose={() => { setActiveLead(null); load(); }}
        />
      )}
    </div>
  );
}

function LeadCard({ lead, onOpen, onDragStart, onDragEnd }) {
  const initials = (lead.name || lead.email)
    .split(/[ @.]/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  const src = lead.source || "unknown";
  const badgeCls = SOURCE_BADGES[src.split(":")[0]] || "bg-[#E5E1DA] text-[#0B192C]";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`lead-card-${lead.id}`}
      className="bg-white border border-[#E5E1DA] p-3 cursor-pointer hover:border-[#0B192C] transition-all"
    >
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 flex-shrink-0 bg-[#0B192C] text-white text-[10px] font-bold flex items-center justify-center">
          {initials || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate text-[#0B192C]">{lead.name || lead.email.split("@")[0]}</p>
          <p className="text-[10px] font-mono text-[#3A4F6B] truncate">{lead.email}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mt-3">
        <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] ${badgeCls}`}>
          {src.length > 14 ? src.slice(0, 14) + "…" : src}
        </span>
        {lead.value > 0 && (
          <span className="text-[10px] font-mono font-bold text-[#0B7C5C]">€{lead.value.toFixed(0)}</span>
        )}
      </div>
      {lead.utm_campaign && (
        <p className="text-[9px] text-[#3A4F6B] mt-1 truncate">↳ {lead.utm_campaign}</p>
      )}
    </div>
  );
}

function LeadDetailModal({ leadId, stages, onClose }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/${leadId}`, { headers: authHdr() });
      setLead(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const updateField = async (patch) => {
    try {
      const { data } = await axios.patch(`${API}/admin/leads/${leadId}`, patch, { headers: authHdr() });
      setLead(data);
    } catch (err) { alert(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/leads/${leadId}/notes`, { text: note.trim() }, { headers: authHdr() });
      setNote("");
      load();
    } catch (err) { alert(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo lead? Operazione irreversibile.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers: authHdr() });
      onClose();
    } catch (err) { alert(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B192C]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full my-8 relative" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
        <button onClick={onClose} data-testid="lead-modal-close" className="absolute top-4 right-4 text-[#3A4F6B] hover:text-[#0B192C] z-10">
          <X size={22} />
        </button>
        {loading || !lead ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin" size={24} /></div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-[#0B192C] to-[#173F5F] text-[#F9F8F6] p-6">
              <p className="overline text-[#E89B6C]">Lead · {fmtDate(lead.created_at)}</p>
              <h3 className="font-serif-display text-3xl mt-2 leading-tight">
                {lead.name || lead.email.split("@")[0]}
              </h3>
              <div className="mt-3 flex flex-wrap gap-3 text-sm text-[#F9F8F6]/80">
                <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-[#E89B6C]" /> {lead.email}</span>
                {lead.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} className="text-[#E89B6C]" /> {lead.phone}</span>}
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Stage selector */}
              <div>
                <p className="overline mb-2">Stage</p>
                <div className="flex flex-wrap gap-2">
                  {stages.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => updateField({ stage: s.id })}
                      data-testid={`stage-btn-${s.id}`}
                      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border transition-all ${
                        lead.stage === s.id ? "text-white" : "text-[#0B192C] hover:bg-[#F4E8D6]/50"
                      }`}
                      style={lead.stage === s.id ? { background: s.color, borderColor: s.color } : { borderColor: "#E5E1DA" }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Source + UTM */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Field label="Fonte" value={lead.source} />
                <Field label="UTM Source" value={lead.utm_source} />
                <Field label="UTM Medium" value={lead.utm_medium} />
                <Field label="UTM Campaign" value={lead.utm_campaign} />
              </div>

              {/* Editable fields */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <p className="overline mb-1">Telefono</p>
                  <input type="tel" defaultValue={lead.phone || ""}
                    onBlur={(e) => e.target.value !== (lead.phone || "") && updateField({ phone: e.target.value })}
                    data-testid="lead-phone-input"
                    className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
                </div>
                <div>
                  <p className="overline mb-1">Valore stimato (€)</p>
                  <input type="number" step="0.01" defaultValue={lead.value || 0}
                    onBlur={(e) => parseFloat(e.target.value) !== (lead.value || 0) && updateField({ value: parseFloat(e.target.value) || 0 })}
                    data-testid="lead-value-input"
                    className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
                </div>
              </div>

              {/* Add note */}
              <div>
                <p className="overline mb-2">Aggiungi nota</p>
                <div className="flex gap-2">
                  <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    placeholder="Es. Ho chiamato, fissato appuntamento martedì..."
                    data-testid="lead-note-input"
                    className="flex-1 px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
                  <button onClick={addNote} disabled={saving || !note.trim()}
                    data-testid="lead-add-note-btn"
                    className="inline-flex items-center gap-1 px-4 py-2 bg-[#0B192C] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#E89B6C] hover:text-[#0B192C] disabled:opacity-60">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Aggiungi
                  </button>
                </div>
              </div>

              {/* History */}
              <div>
                <p className="overline mb-2">Storico ({(lead.history || []).length})</p>
                <ul className="max-h-72 overflow-y-auto border border-[#E5E1DA] divide-y divide-[#E5E1DA]">
                  {[...(lead.history || [])].reverse().map((h, i) => (
                    <li key={i} className="p-3 text-xs flex gap-3" data-testid={`history-${i}`}>
                      <span className="font-mono text-[#3A4F6B] flex-shrink-0">{fmtDate(h.at)}</span>
                      <span className="flex-1">
                        {h.type === "note" && <><MessageSquare size={11} className="inline mr-1 text-[#0E7C8F]" /> {h.text}</>}
                        {h.type === "stage_change" && <><TrendingUp size={11} className="inline mr-1 text-[#E89B6C]" /> Stage: <code className="font-mono">{h.from} → {h.to}</code></>}
                        {h.type === "created" && <>✨ Creato da <code className="font-mono">{h.source}</code></>}
                        {h.type === "touch" && <>👋 Touch da <code className="font-mono">{h.source}</code> {h.note && `· ${h.note}`}</>}
                        {h.by && <span className="text-[#3A4F6B]"> · {h.by}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-center justify-between border-t border-[#E5E1DA] pt-4">
                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 px-4 py-2 border border-[#0E7C8F] text-[#0E7C8F] text-xs uppercase tracking-[0.18em] hover:bg-[#0E7C8F] hover:text-white">
                  <Mail size={13} /> Email
                </a>
                <button onClick={remove} data-testid="lead-delete-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.18em] hover:bg-red-700 hover:text-white">
                  <Trash2 size={13} /> Elimina lead
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-[#F4E8D6]/40 p-2 border border-[#E5E1DA]">
      <p className="overline text-[9px] text-[#3A4F6B]">{label}</p>
      <p className="font-mono text-xs mt-0.5 truncate" title={value}>{value || "—"}</p>
    </div>
  );
}
